import { router } from '@inertiajs/vue3'

// eslint-disable-next-line no-undef
const apiKey = VITE_POSTHOG_API_KEY
// eslint-disable-next-line no-undef
const host = VITE_POSTHOG_API_URL

const PUBLIC_PAGES = new Set([
  'auth/Signin',
  'auth/Signup',
  'auth/UserActivate',
  'auth/ForgotPassword',
  'auth/ResetPassword',
  'home/Contact',
  'home/Setup',
])

const MODES = {
  anonymous: { persistence: 'memory', disable_session_recording: true },
  identified: { persistence: 'localStorage+cookie', disable_session_recording: false },
}

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

let posthog = null
let mode = null
let organizationId = null
const capturedOnce = new Set()

function bootstrapFromUrl() {
  const distinctID = new URLSearchParams(location.search).get('ph')
  return UUID_PATTERN.test(distinctID) ? { bootstrap: { distinctID } } : {}
}

async function enable(nextMode) {
  if (!posthog) {
    posthog = (await import('posthog-js')).default
    posthog.init(apiKey, {
      api_host: host,
      defaults: '2025-05-24',
      capture_pageview: false,
      ...MODES[nextMode],
      ...bootstrapFromUrl(),
    })
  }
  else if (mode !== nextMode) {
    posthog.set_config(MODES[nextMode])
    if (nextMode === 'identified') {
      posthog.startSessionRecording()
    }
    else {
      posthog.stopSessionRecording()
    }
  }
  mode = nextMode
  if (posthog.has_opted_out_capturing()) {
    posthog.opt_in_capturing({ captureEventName: false })
  }
}

function pauseOnOrganizationChange({ detail: { visit } }) {
  if (!visit.prefetch && !visit.url.pathname.startsWith(`/${organizationId}/`)) {
    posthog?.opt_out_capturing()
  }
}

async function syncPosthog({ component, props: { posthog: tracking, user, organization } }) {
  if (PUBLIC_PAGES.has(component)) {
    await enable('anonymous')
  }
  else if (tracking === undefined) {
    return
  }
  else if (!tracking) {
    posthog?.opt_out_capturing()
    return
  }
  else {
    organizationId = organization.id
    await enable('identified')
    posthog.identify(user.id, { organization_id: organization.id })
  }
  posthog.capture('$pageview')
}

export function capturePosthog(event, properties) {
  posthog?.capture(event, properties)
}

export function capturePosthogOnce(event) {
  if (posthog && !posthog.has_opted_out_capturing() && !capturedOnce.has(event)) {
    capturedOnce.add(event)
    posthog.capture(event)
  }
}

export function getPosthogDistinctId() {
  return posthog?.get_distinct_id()
}

export function resetPosthog() {
  posthog?.reset()
}

export default function usePosthog() {
  if (apiKey && host) {
    router.on('start', pauseOnOrganizationChange)
    router.on('navigate', event => syncPosthog(event.detail.page))
  }
}
